// @ts-check
// Adaptador de compatibilidad hacia atrás IR -> grafo de arcos (TSK-0192,
// materializa DEC-069 punto 5 -- docs/generated/decisions.md#DEC-069).
//
// Contexto: hoy la conectividad de un diagrama es IMPLÍCITA en el árbol IR
// estructurado ya existente (`editor-model.js`): el orden de
// `Secuencia.sentencias`, `Condicional.rama_si`/`rama_sino` y `cuerpo` de
// `Bucle*`. DEC-069 introduce, en fases, un grafo EXPLÍCITO de arcos
// (`arrow-connection-graph.js`, TSK-0191) que en TSK-0193 compilará de vuelta
// a esa misma IR (`compilarGrafoAIr`). Este módulo es el adaptador INVERSO:
// dado un `Programa` ya existente (los diagramas guardados hoy, incluidos el
// autoguardado de localStorage y las 52 soluciones de referencia), deriva el
// grafo de arcos equivalente + la lista plana de nodos-bloque, para que ese
// contenido siga siendo cargable/editable bajo el nuevo modelo de flechas.
//
// Puro, sin DOM ni `fetch`: solo depende de la API pública ya aprobada de
// `arrow-connection-graph.js` (`PUERTO`, `crearArco`, `crearGrafoArcos`,
// `agregarArco`) y de `TIPO_NODO` de `editor-model.js` (solo lectura, ninguna
// de las dos rutas se edita aquí: son "rutas_prohibidas"/dependencias de la
// ficha de esta tarea).
//
// Algoritmo (recorrido recursivo de `Secuencia.sentencias`, ver
// `procesarCadena`/`procesarNodo` más abajo): cada "cadena" (la `Secuencia`
// raíz del programa, una rama de `Condicional`, o el `cuerpo` de un `Bucle*`)
// recibe un ORIGEN (el puerto que debe conectar con la ENTRADA de su primer
// bloque: `inicio.SALIDA`, `Condicional.VERDADERO/FALSO` o `Bucle*.CUERPO`) y
// un DESTINO final (el puerto al que debe llegar la SALIDA de su último
// bloque efectivo: el siguiente hermano, `fin.ENTRADA`, o `null` para el
// `cuerpo` de un bucle, cuyo "retorno" es IMPLÍCITO -- DEC-069 punto 1 -- y
// por tanto NO se dibuja ningún arco en ese punto).
//
// Ambigüedades resueltas (repórtense también en la respuesta final del
// agente ejecutor):
//
// 1. Alcance del recorrido: `derivarGrafoDeIr` solo recorre `programa.cuerpo`
//    (la `Secuencia` raíz del diagrama principal). `programa.declaraciones` y
//    `programa.funciones` NO participan del grafo de flujo -- igual que en
//    `arrow-connection-graph.js`/DEC-069, que solo modela el flujo de
//    sentencias de UN cuerpo (`Secuencia`/`Condicional`/`Bucle*`), no la
//    lista de declaraciones/funciones del `Programa` envoltorio. Derivar el
//    grafo de cada `Funcion.cuerpo` por separado (si el editor de funciones
//    lo necesita) es responsabilidad de quien orqueste esa llamada, invocando
//    este mismo adaptador con `funcion.cuerpo` en vez de `programa.cuerpo`
//    (de ahí que la función auxiliar exportada `derivarGrafoDeCuerpo` acepte
//    directamente una `Secuencia`, ver más abajo).
// 2. `Comentario` no participa del flujo (sin puertos, DEC-069 punto 1): se
//    incluye en la lista plana de nodos-bloque (es un bloque visible en el
//    canvas) pero se salta al construir la cadena de arcos -- ni recibe ni
//    origina ningún arco.
// 3. Rama `rama_sino` ausente (`null`, "sin sino"): el puerto `FALSO` del
//    `Condicional` conecta DIRECTAMENTE al punto de convergencia (siguiente
//    hermano del condicional, o `fin`), sin fabricar un bloque/arco
//    intermedio -- criterio explícito de la ficha ("sin fabricar arcos
//    falsos").
// 4. `destinoFinal = null` (retorno implícito del `cuerpo` de un `Bucle*`,
//    DEC-069 punto 1): un bloque normal tolera terminar sin arco de salida
//    (`SALIDA` es como mucho 1, nunca obligatoria), así que `conectar()`
//    simplemente ignora un destino `null`. Pero una rama de `Condicional`
//    "vacía" o "ausente" (`rama_sino = null`, o una `Secuencia` sin
//    sentencias) resuelve su único arco de puerto (`VERDADERO`/`FALSO`)
//    conectando DIRECTAMENTE a `destinoFinal` (punto 3); si en ese momento
//    `destinoFinal` es `null` (porque ese `Condicional` es, a su vez, la
//    última sentencia del `cuerpo` de un bucle), esa rama se queda sin
//    ningún arco -- `validarGrafoArcos` lo reportaría como
//    `CONDICIONAL_RAMA_FALTANTE`. Limitación estructural conocida de esa
//    combinación exacta (rama vacía/ausente como última sentencia de un
//    cuerpo de bucle): no existe ningún nodo real al que enganchar esa rama
//    sin fabricar un arco falso (enganchar al propio bucle violaría
//    `MULTIPLES_ENTRADAS`/`CICLO`). Los tests de esta tarea documentan este
//    caso explícitamente en vez de ocultarlo (no lo usan como fixture de
//    "programa válido").
//
// 5. **CONTRADICCIÓN DETECTADA entre esta ficha y `validarGrafoArcos`
//    (TSK-0191, ya aprobado, ruta prohibida para esta tarea) -- reportada
//    aquí y en la respuesta final del agente ejecutor, no resuelta en
//    silencio (instrucción del proyecto):** el criterio de aceptación
//    "último de cada rama -> convergencia (siguiente hermano del Condicional
//    o Fin)" exige que AMBAS ramas de un `Condicional` con continuación real
//    emitan un arco hacia el MISMO nodo de convergencia (el siguiente
//    hermano, o `fin`). Pero `validarGrafoArcos` exige, sin excepción ni
//    hueco para puntos de convergencia legítimos, `MULTIPLES_ENTRADAS` como
//    mucho 1 arco entrante a cualquier bloque normal, y EXACTAMENTE 1 a
//    `fin`. Estas dos reglas son matemáticamente incompatibles en cuanto el
//    `Condicional` tiene continuación real: dos arcos (uno por rama) hacia el
//    mismo nodo de convergencia SIEMPRE disparan `MULTIPLES_ENTRADAS` (o la
//    regla de "exactamente 1" de `fin`). Esto no es un defecto de este
//    módulo -- `derivarGrafoDeIr` produce, deliberadamente, el grafo
//    semánticamente correcto (el que sí preserva round-trip con el futuro
//    compilador `compilarGrafoAIr`, TSK-0193) -- sino un hueco de diseño de
//    `validarGrafoArcos` que ninguna combinación de rutas permitidas de esta
//    ficha puede cerrar (no se puede editar `arrow-connection-graph.js`, ni
//    fabricar un tipo de nodo "unión"/"conector" no reconocido por
//    `PUERTOS_POR_TIPO` sin que `puertosDeNodo` lance `TypeError`). Único
//    caso en el que un `Condicional` con continuación real SÍ pasa
//    `validarGrafoArcos` sin violaciones: cuando el propio `Condicional` es
//    la última sentencia del `cuerpo` de un `Bucle*` Y ambas ramas son
//    no vacías -- el "retorno implícito" (punto 4) hace que NINGUNA rama
//    dibuje el arco de convergencia final, evitando la colisión (ver test
//    "pasa validarGrafoArcos sin violaciones" en
//    `arrow-graph-derivation.spec.js`). Solución propuesta (fuera del
//    alcance/rutas de esta ficha, requiere architecture-reviewer): o bien
//    relajar `MULTIPLES_ENTRADAS`/la regla de `fin` para permitir >1 arco
//    entrante cuando todos los arcos entrantes provienen de las ramas de un
//    mismo `Condicional` ancestro (post-dominador local), o bien introducir
//    un tipo de nodo "unión"/conector explícito en `PUERTOS_POR_TIPO` con
//    entrada múltiple y salida única. Los tests de este módulo documentan
//    explícitamente la violación esperada en los casos afectados en vez de
//    ocultarla.

import { PUERTO, crearArco, crearGrafoArcos, agregarArco } from './arrow-connection-graph.js';
import { TIPO_NODO } from './editor-model.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 * @typedef {import('./arrow-connection-graph.js').GrafoArcos} GrafoArcos
 * @typedef {import('./arrow-connection-graph.js').ValorPuerto} ValorPuerto
 * @typedef {import('./arrow-connection-graph.js').NodoOClave} NodoOClave
 */

/**
 * Resultado de la derivación: el grafo de arcos equivalente y la lista plana
 * de nodos-bloque (bloques reales + pseudo-anclas `'inicio'`/`'fin'`), lista
 * para pasar directamente a `validarGrafoArcos(nodos, grafo)`.
 * @typedef {{ grafo: GrafoArcos, nodos: NodoOClave[] }} GrafoDerivado
 */

/** @typedef {{ nodoId: string, puerto: ValorPuerto }} Extremo */

const CLAVE_INICIO = 'inicio';
const CLAVE_FIN = 'fin';

/** Nodos de bucle: mismo conjunto que `NODOS_BUCLE` de
 * `arrow-connection-graph.js`, redefinido aquí de forma autocontenida (ese
 * módulo no lo exporta; se reutiliza únicamente su API pública). */
const NODOS_BUCLE = /** @type {Set<string>} */ (
  new Set([TIPO_NODO.BUCLE_MIENTRAS, TIPO_NODO.BUCLE_FIN_PARA, TIPO_NODO.REPETIR_HASTA])
);

/** Tipos de "bloque normal" (1 ENTRADA + 1 SALIDA, DEC-069 punto 1) que
 * pueden aparecer como sentencia dentro de una `Secuencia`. */
const TIPOS_BLOQUE_NORMAL = /** @type {Set<string>} */ (
  new Set([
    TIPO_NODO.ENTRADA,
    TIPO_NODO.SALIDA,
    TIPO_NODO.ASIGNACION,
    TIPO_NODO.OPERACION,
    TIPO_NODO.LLAMADA,
    TIPO_NODO.RETORNO,
  ])
);

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {NodoModelo}
 */
function requerirNodoPrograma(valor, nombreCampo) {
  if (
    typeof valor !== 'object' ||
    valor === null ||
    /** @type {Record<string, unknown>} */ (valor).nodo !== TIPO_NODO.PROGRAMA
  ) {
    throw new TypeError(`${nombreCampo} debe ser un nodo Programa (con nodo: 'Programa')`);
  }
  return /** @type {NodoModelo} */ (valor);
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {NodoModelo}
 */
function requerirNodoSecuencia(valor, nombreCampo) {
  if (
    typeof valor !== 'object' ||
    valor === null ||
    /** @type {Record<string, unknown>} */ (valor).nodo !== TIPO_NODO.SECUENCIA ||
    !Array.isArray(/** @type {Record<string, unknown>} */ (valor).sentencias)
  ) {
    throw new TypeError(`${nombreCampo} debe ser un nodo Secuencia (con nodo: 'Secuencia' y sentencias: [])`);
  }
  return /** @type {NodoModelo} */ (valor);
}

/**
 * Acumulador mutable interno de esta función (no se expone: cada llamada a
 * `derivarGrafoDeCuerpo`/`derivarGrafoDeIr` construye el suyo propio), que
 * envuelve las operaciones inmutables ya existentes de
 * `arrow-connection-graph.js` sin mutar directamente ningún grafo.
 */
class ContextoDerivacion {
  constructor() {
    /** @type {GrafoArcos} */
    this.grafo = crearGrafoArcos();
    /** @type {NodoOClave[]} */
    this.nodos = [];
    /** @type {Set<string>} */
    this.idsVistos = new Set();
  }

  /** @param {NodoOClave} nodoOClave */
  registrarNodo(nodoOClave) {
    const id = typeof nodoOClave === 'string' ? nodoOClave : nodoOClave.id;
    if (this.idsVistos.has(id)) return;
    this.idsVistos.add(id);
    this.nodos.push(nodoOClave);
  }

  /**
   * Añade un arco `origen -> destino`. Si `destino` es `null`, no hace nada
   * (ver ambigüedad 4 de la cabecera del módulo: el retorno implícito de un
   * bucle, o una rama de Condicional sin convergencia real disponible, no
   * fabrican un arco falso).
   * @param {Extremo} origen
   * @param {Extremo | null} destino
   */
  conectar(origen, destino) {
    if (destino === null) return;
    const arco = crearArco({
      desdeNodoId: origen.nodoId,
      desdePuerto: origen.puerto,
      hastaNodoId: destino.nodoId,
      hastaPuerto: destino.puerto,
    });
    this.grafo = agregarArco(this.grafo, arco);
  }
}

/**
 * Procesa una "cadena" de sentencias (la `Secuencia` raíz, una rama de
 * `Condicional`, o el `cuerpo` de un `Bucle*`): registra cada sentencia
 * (incluidos los `Comentario`, que no participan del flujo) en la lista
 * plana de nodos-bloque, y encadena arcos entre hermanos consecutivos que sí
 * participan del flujo, desde `origen` hasta `destinoFinal`.
 * @param {ContextoDerivacion} contexto
 * @param {NodoModelo[]} sentencias
 * @param {Extremo} origen
 * @param {Extremo | null} destinoFinal
 */
function procesarCadena(contexto, sentencias, origen, destinoFinal) {
  for (const sentencia of sentencias) contexto.registrarNodo(sentencia);

  const conectables = sentencias.filter((sentencia) => sentencia.nodo !== TIPO_NODO.COMENTARIO);

  if (conectables.length === 0) {
    contexto.conectar(origen, destinoFinal);
    return;
  }

  contexto.conectar(origen, { nodoId: conectables[0].id, puerto: PUERTO.ENTRADA });

  for (let indice = 0; indice < conectables.length; indice += 1) {
    const sentencia = conectables[indice];
    const esUltima = indice === conectables.length - 1;
    const salidaSiguiente = esUltima
      ? destinoFinal
      : { nodoId: conectables[indice + 1].id, puerto: PUERTO.ENTRADA };
    procesarNodo(contexto, sentencia, salidaSiguiente);
  }
}

/**
 * Procesa una única sentencia ya registrada como nodo-bloque, emitiendo los
 * arcos de su(s) puerto(s) de salida hacia `salidaSiguiente` (el siguiente
 * hermano de su cadena contenedora, o el destino final de esa cadena).
 * @param {ContextoDerivacion} contexto
 * @param {NodoModelo} sentencia
 * @param {Extremo | null} salidaSiguiente
 */
function procesarNodo(contexto, sentencia, salidaSiguiente) {
  if (sentencia.nodo === TIPO_NODO.CONDICIONAL) {
    const ramaSi = requerirNodoSecuencia(sentencia.rama_si, `${sentencia.id}.rama_si`);
    procesarCadena(
      contexto,
      /** @type {NodoModelo[]} */ (ramaSi.sentencias),
      { nodoId: sentencia.id, puerto: PUERTO.VERDADERO },
      salidaSiguiente
    );

    const ramaSino = sentencia.rama_sino;
    if (ramaSino === null || ramaSino === undefined) {
      // DEC-069/ficha: rama_sino ausente -> FALSO conecta directo a la
      // convergencia, sin fabricar arcos falsos.
      contexto.conectar({ nodoId: sentencia.id, puerto: PUERTO.FALSO }, salidaSiguiente);
    } else {
      const secuenciaSino = requerirNodoSecuencia(ramaSino, `${sentencia.id}.rama_sino`);
      procesarCadena(
        contexto,
        /** @type {NodoModelo[]} */ (secuenciaSino.sentencias),
        { nodoId: sentencia.id, puerto: PUERTO.FALSO },
        salidaSiguiente
      );
    }
    return;
  }

  if (NODOS_BUCLE.has(sentencia.nodo)) {
    const cuerpo = requerirNodoSecuencia(sentencia.cuerpo, `${sentencia.id}.cuerpo`);
    // Retorno implícito (DEC-069 punto 1): destinoFinal = null, ningún arco
    // dibujado al final del cuerpo.
    procesarCadena(contexto, /** @type {NodoModelo[]} */ (cuerpo.sentencias), {
      nodoId: sentencia.id,
      puerto: PUERTO.CUERPO,
    }, null);
    contexto.conectar({ nodoId: sentencia.id, puerto: PUERTO.SALIDA }, salidaSiguiente);
    return;
  }

  if (TIPOS_BLOQUE_NORMAL.has(sentencia.nodo)) {
    contexto.conectar({ nodoId: sentencia.id, puerto: PUERTO.SALIDA }, salidaSiguiente);
    return;
  }

  throw new TypeError(
    `derivarGrafoDeIr: tipo de nodo '${sentencia.nodo}' no soportado como sentencia de flujo`
  );
}

/**
 * Deriva el grafo de arcos equivalente + la lista plana de nodos-bloque a
 * partir de una `Secuencia` ya existente (típicamente el `cuerpo` de un
 * `Programa` o de una `Funcion`), incluyendo las pseudo-anclas `'inicio'`/
 * `'fin'` (DEC-069 punto 5). Módulo puro: no toca DOM ni backend.
 * @param {NodoModelo} cuerpo
 * @returns {GrafoDerivado}
 */
export function derivarGrafoDeCuerpo(cuerpo) {
  const raiz = requerirNodoSecuencia(cuerpo, 'cuerpo');
  const contexto = new ContextoDerivacion();

  contexto.registrarNodo(CLAVE_INICIO);
  contexto.registrarNodo(CLAVE_FIN);

  procesarCadena(
    contexto,
    /** @type {NodoModelo[]} */ (raiz.sentencias),
    { nodoId: CLAVE_INICIO, puerto: PUERTO.SALIDA },
    { nodoId: CLAVE_FIN, puerto: PUERTO.ENTRADA }
  );

  return { grafo: contexto.grafo, nodos: contexto.nodos };
}

/**
 * Deriva el grafo de arcos equivalente + la lista plana de nodos-bloque a
 * partir de un `Programa` completo (recorre `programa.cuerpo`, la `Secuencia`
 * raíz -- ver ambigüedad 1 de la cabecera del módulo sobre
 * `declaraciones`/`funciones`). Compatibilidad hacia atrás de DEC-069 punto
 * 5: para que los diagramas ya guardados (autoguardado local + 52 soluciones
 * de referencia) sigan siendo cargables/editables bajo el nuevo modelo de
 * flechas. Puro: no toca DOM ni backend.
 * @param {NodoModelo} programa
 * @returns {GrafoDerivado}
 */
export function derivarGrafoDeIr(programa) {
  const nodoPrograma = requerirNodoPrograma(programa, 'programa');
  return derivarGrafoDeCuerpo(/** @type {NodoModelo} */ (nodoPrograma.cuerpo));
}
